import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, User, MapPin, Activity, Database, Link as LinkIcon, Copy, AlertTriangle, Check, Mail } from "lucide-react";
import { toast } from "sonner";

const ACTION_COLORS = {
  create: "bg-emerald-100 text-emerald-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-rose-100 text-rose-700",
  login: "bg-purple-100 text-purple-700",
  logout: "bg-slate-100 text-slate-600 dark:text-slate-300",
  client_error: "bg-rose-100 text-rose-700",
};
const actionClass = (a) => {
  const key = Object.keys(ACTION_COLORS).find((k) => (a || "").includes(k));
  return ACTION_COLORS[key] || "bg-slate-100 text-slate-600 dark:text-slate-300";
};

export default function AuditLogDetailModal({ logId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [engContact, setEngContact] = useState({ email: "engineering@glpflo.org", label: "Engineering" });

  useEffect(() => {
    if (!open || !logId) { setData(null); setCopied(false); return; }
    setLoading(true);
    API.get(`/audit-logs/${logId}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    API.get("/admin/engineering-contact").then(({ data }) => setEngContact(data)).catch(() => {});
  }, [logId, open]);

  const isError = data?.log?.action === "client_error";

  const buildReport = () => {
    if (!data?.log) return "";
    return [
      `[GLPFlo Client Error]`,
      `Time: ${new Date(data.log.timestamp).toLocaleString()}`,
      `User: ${data.log.user_name || "-"} (${data.log.role || "?"})`,
      `IP: ${data.log.ip_address || "-"}`,
      `Log ID: ${data.log.id}`,
      ``,
      data.log.details || "",
    ].join("\n");
  };

  const copyStack = async () => {
    const payload = buildReport();
    if (!payload) return;
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      toast.success("Stack trace copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed"); }
  };

  const emailEngineering = () => {
    if (!data?.log) return;
    const subject = `[GLPFlo Client Error] ${data.log.id} — ${new Date(data.log.timestamp).toLocaleString()}`;
    const body = `${buildReport()}\n\n— Reported from Admin Audit Logs`;
    const href = `mailto:${engContact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
    toast.success(`Opening email to ${engContact.label}…`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="audit-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-600" /> Audit Log Detail
          </DialogTitle>
        </DialogHeader>

        {loading && <p className="text-slate-400 dark:text-slate-500 text-sm py-8 text-center">Loading...</p>}

        {!loading && data?.log && (
          <div className="space-y-5 min-w-0">
            {/* Severity banner for errors */}
            {isError && (
              <div className="rounded-xl bg-gradient-to-r from-rose-50 to-red-50 border border-rose-200 p-4 flex flex-col sm:flex-row sm:items-start gap-3" data-testid="severity-banner">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="relative flex h-3 w-3 mt-1 flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-rose-500"></span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-rose-800 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Client-side error detected</p>
                    <p className="text-xs text-rose-700 mt-0.5">A frontend exception was caught by the ErrorBoundary. Copy the stack trace or email engineering directly.</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0 sm:self-center">
                  <Button size="sm" onClick={copyStack} variant="outline" className="rounded-lg border-rose-300 text-rose-700 hover:bg-rose-100" data-testid="copy-stack-btn">
                    {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
                  </Button>
                  <Button size="sm" onClick={emailEngineering} className="rounded-lg bg-rose-600 hover:bg-rose-700 text-white" data-testid="email-engineering-btn">
                    <Mail className="w-4 h-4 mr-1" /> Email {engContact.label}
                  </Button>
                </div>
              </div>
            )}

            {/* Header card */}
            <div className={`rounded-xl border p-4 ${isError ? "bg-rose-50/40 border-rose-100" : "bg-gradient-to-br from-purple-50 to-white border-purple-100"}`}>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <Badge className={actionClass(data.log.action)}>{data.log.action.replace(/_/g, " ")}</Badge>
                {data.log.resource_type && <Badge variant="outline" className="text-slate-600 dark:text-slate-300">{data.log.resource_type}</Badge>}
              </div>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <Row icon={Clock} label="Timestamp" value={new Date(data.log.timestamp).toLocaleString()} />
                <Row icon={User} label="User" value={`${data.log.user_name || "-"} · ${data.log.role || "?"}`} />
                <Row icon={MapPin} label="IP address" value={data.log.ip_address || "-"} mono />
                <Row icon={Database} label="Resource ID" value={data.log.resource_id || "-"} mono />
              </div>
            </div>

            {/* Raw details */}
            {data.log.details && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide">Action Details</p>
                  {!isError && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 hover:text-slate-900 dark:text-white" onClick={copyStack} data-testid="copy-details-btn">
                      {copied ? <><Check className="w-3.5 h-3.5 mr-1" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
                    </Button>
                  )}
                </div>
                <pre className="bg-slate-900 text-slate-200 rounded-xl p-4 text-xs overflow-x-auto whitespace-pre-wrap break-all max-w-full" data-testid="audit-details-raw">{data.log.details}</pre>
              </div>
            )}

            {/* Linked resource current state */}
            {data.resource && (
              <div>
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <LinkIcon className="w-3 h-3" /> Current state of {data.resource.collection} record
                </p>
                <pre className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-100 rounded-xl p-4 text-xs overflow-x-auto max-h-80" data-testid="audit-resource">{JSON.stringify(data.resource.document, null, 2)}</pre>
              </div>
            )}

            {/* Related in time */}
            {data.related && data.related.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide mb-2">
                  Related activity ({data.related.length}) — same user, ±30 minutes
                </p>
                <div className="space-y-1.5 max-h-64 overflow-y-auto border border-slate-100 dark:border-slate-800/60 rounded-xl p-2" data-testid="audit-related">
                  {data.related.map((r) => (
                    <div key={r.id} className="flex items-start gap-3 text-xs py-1.5 px-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-slate-800/40">
                      <span className="text-slate-400 dark:text-slate-500 whitespace-nowrap font-mono">{new Date(r.timestamp).toLocaleTimeString()}</span>
                      <Badge className={`${actionClass(r.action)} text-[10px] whitespace-nowrap`}>{r.action.replace(/_/g, " ")}</Badge>
                      <span className="text-slate-600 dark:text-slate-300 truncate flex-1">{r.details || r.resource_type || "-"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!data.related || data.related.length === 0) && !data.resource && !data.log.details && (
              <p className="text-slate-400 dark:text-slate-500 text-sm text-center py-4">No additional context available.</p>
            )}
          </div>
        )}
        {!loading && !data && <p className="text-slate-400 dark:text-slate-500 text-sm py-8 text-center">Log not found.</p>}
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon: Icon, label, value, mono = false }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 text-slate-400 dark:text-slate-500 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-slate-500 dark:text-slate-400 dark:text-slate-500 uppercase tracking-wide">{label}</p>
        <p className={`text-sm text-slate-900 dark:text-white ${mono ? "font-mono" : ""} break-words`}>{value}</p>
      </div>
    </div>
  );
}
